"use client";

import { useState } from "react";
import {
  DataTable,
  Notice,
  type Option,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { CARD, HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { CurrencyField, useMoney } from "@/components/tools/finance/shared";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput, parseNumberOr } from "@/lib/parse-number";
import { capm } from "@/lib/tool-kit/accounting/capm";
import {
  type ComponentKey,
  type WaccInput,
  type WaccResult,
  wacc,
} from "@/lib/tool-kit/accounting/wacc";

/** English strings. A translated content file overrides these through `ui`. */
export const WACC_CALCULATOR_STRINGS = {
  inputsHeading: "Capital structure and costs",
  currency: "Currency",
  plainNumbers: "No currency (plain numbers)",
  equity: "Market value of equity",
  equityHint:
    "Share price × shares outstanding. Amounts or percentages both work: only the proportions matter.",
  debt: "Market value of debt",
  debtHint:
    "Book value of debt is a common stand-in when market value is not available.",
  preferred: "Preferred stock (optional)",
  costMethodLabel: "Cost of equity",
  costEnter: "I know it",
  costCapm: "Calculate it with CAPM",
  costEquity: "Cost of equity (% a year)",
  riskFree: "Risk-free rate (%)",
  beta: "Beta",
  marketReturn: "Expected market return (%)",
  costDebt: "Pre-tax cost of debt (% a year)",
  costDebtHint:
    "The interest rate the firm pays on new borrowing, or the yield to maturity on its bonds.",
  costPreferred: "Cost of preferred stock (%)",
  costPreferredHint: "Annual preferred dividend ÷ preferred price.",
  taxRate: "Corporate tax rate (%)",
  convention:
    "WACC = E/V × Re + D/V × Rd × (1 − tax rate) + P/V × Rp. Interest is tax-deductible, so debt enters at its after-tax cost; dividends are not deductible.",
  resultsHeading: "Results",
  waccLabel: "WACC",
  afterTaxDebt: "After-tax cost of debt",
  equityUsed: "Cost of equity used",
  taxEffect: "Reduction from the tax shield",
  totalCapital: "Total capital (V)",
  workingHeading: "The working",
  workAfterTax:
    "After-tax cost of debt = Rd × (1 − tax rate) = {rd} × (1 − {tax}) = {result}",
  workWacc: "WACC = {terms} = {wacc}",
  workCapm:
    "Cost of equity (CAPM) = Rf + β × (Rm − Rf) = {rf} + {beta} × ({rm} − {rf}) = {re}",
  tableHeading: "Contribution of each source",
  sourceColumn: "Source",
  amountColumn: "Amount",
  weightColumn: "Weight",
  costColumn: "Cost before tax",
  usedColumn: "Cost used",
  contributionColumn: "Weighted cost",
  totalRow: "Total",
  nameEquity: "Equity",
  nameDebt: "Debt",
  namePreferred: "Preferred stock",
  needAmounts:
    "Enter the market value of equity and debt, each 0 or more with a total above zero.",
  needRates:
    "Enter the costs and the tax rate as percentages, with a tax rate from 0 to 100.",
} as const;

export type WaccCalculatorStrings = typeof WACC_CALCULATOR_STRINGS;
type Strings = WaccCalculatorStrings;
type CostMethod = "enter" | "capm";

const NAMES: Record<ComponentKey, keyof Strings> = {
  equity: "nameEquity",
  debt: "nameDebt",
  preferred: "namePreferred",
};

interface Form {
  equity: string;
  debt: string;
  preferred: string;
  method: CostMethod;
  costEquity: string;
  rf: string;
  beta: string;
  rm: string;
  costDebt: string;
  costPreferred: string;
  tax: string;
}

type Parsed =
  | { input: WaccInput; capm: { rf: number; beta: number; rm: number } | null }
  | { error: keyof Strings };

function parseForm(form: Form): Parsed {
  const equity = parseNumberInput(form.equity);
  const debt = parseNumberInput(form.debt);
  const preferred = parseNumberOr(form.preferred, 0);
  if (equity === null || debt === null) {
    return { error: "needAmounts" };
  }
  const rf = parseNumberInput(form.rf);
  const beta = parseNumberInput(form.beta);
  const rm = parseNumberInput(form.rm);
  const useCapm = form.method === "capm";
  const costEquityText = parseNumberInput(form.costEquity);
  const costDebt = parseNumberInput(form.costDebt);
  const tax = parseNumberInput(form.tax);
  const capmInputs =
    rf !== null && beta !== null && rm !== null
      ? { rf: rf / 100, beta, rm: rm / 100 }
      : null;
  let costEquity: number | null = null;
  if (useCapm && capmInputs) {
    costEquity = capm(
      capmInputs.rf,
      capmInputs.beta,
      capmInputs.rm
    ).expectedReturn;
  } else if (!useCapm && costEquityText !== null) {
    costEquity = costEquityText / 100;
  }
  if (costEquity === null || costDebt === null || tax === null) {
    return { error: "needRates" };
  }
  return {
    input: {
      equity,
      debt,
      preferred,
      costEquity,
      costDebt: costDebt / 100,
      costPreferred: parseNumberOr(form.costPreferred, 0) / 100,
      taxRate: tax / 100,
    },
    capm: useCapm ? capmInputs : null,
  };
}

function Results({
  result,
  input,
  capmInputs,
  currency,
  strings,
}: {
  result: WaccResult;
  input: WaccInput;
  capmInputs: { rf: number; beta: number; rm: number } | null;
  currency: string;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const money = useMoney(currency);
  const pct = (v: number, max = 4) =>
    `${fmt(v * 100, { maximumFractionDigits: max })}%`;
  const terms = result.components
    .map((c) => `${pct(c.weight, 2)} × ${pct(c.effectiveCost)}`)
    .join(" + ");
  const rows = result.components.map((c) => [
    strings[NAMES[c.key]],
    money(c.value),
    pct(c.weight, 2),
    pct(c.cost),
    pct(c.effectiveCost),
    pct(c.contribution),
  ]);
  rows.push([
    strings.totalRow,
    money(result.total),
    "100%",
    "",
    "",
    pct(result.wacc),
  ]);
  return (
    <section aria-live="polite" className="space-y-5">
      <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
      <StatGrid>
        <StatCard emphasis label={strings.waccLabel} value={pct(result.wacc)} />
        <StatCard label={strings.equityUsed} value={pct(input.costEquity)} />
        <StatCard
          label={strings.afterTaxDebt}
          value={pct(result.afterTaxCostOfDebt)}
        />
        <StatCard
          label={strings.taxEffect}
          value={pct(result.taxShieldEffect)}
        />
        <StatCard label={strings.totalCapital} value={money(result.total)} />
      </StatGrid>
      <div className={`${CARD} space-y-1`}>
        <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
        {capmInputs ? (
          <p className="font-mono text-muted-foreground text-sm leading-relaxed">
            {t(strings.workCapm, {
              rf: pct(capmInputs.rf),
              beta: fmt(capmInputs.beta, { maximumFractionDigits: 4 }),
              rm: pct(capmInputs.rm),
              re: pct(input.costEquity),
            })}
          </p>
        ) : null}
        <p className="font-mono text-muted-foreground text-sm leading-relaxed">
          {t(strings.workAfterTax, {
            rd: pct(input.costDebt),
            tax: pct(input.taxRate),
            result: pct(result.afterTaxCostOfDebt),
          })}
        </p>
        <p className="font-mono text-muted-foreground text-sm leading-relaxed">
          {t(strings.workWacc, { terms, wacc: pct(result.wacc) })}
        </p>
      </div>
      <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
      <DataTable
        head={[
          strings.sourceColumn,
          strings.amountColumn,
          strings.weightColumn,
          strings.costColumn,
          strings.usedColumn,
          strings.contributionColumn,
        ]}
        rows={rows}
      />
    </section>
  );
}

export default function WaccCalculatorWidget() {
  const strings = useToolUi(WACC_CALCULATOR_STRINGS);
  const [currency, setCurrency] = useState("USD");
  const [form, setForm] = useState<Form>({
    equity: "600000",
    debt: "400000",
    preferred: "",
    method: "capm",
    costEquity: "9",
    rf: "3",
    beta: "1.2",
    rm: "8",
    costDebt: "5",
    costPreferred: "",
    tax: "25",
  });
  const update = (patch: Partial<Form>) =>
    setForm((old) => ({ ...old, ...patch }));
  const methods: Option<CostMethod>[] = [
    { value: "enter", label: strings.costEnter },
    { value: "capm", label: strings.costCapm },
  ];
  const parsed = parseForm(form);
  const result = "input" in parsed ? wacc(parsed.input) : null;

  return (
    <div className="space-y-6">
      <section className={`${CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.inputsHeading}</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <CurrencyField
            id="wacc-currency"
            label={strings.currency}
            onChange={setCurrency}
            plainLabel={strings.plainNumbers}
            value={currency}
          />
          <TextField
            hint={strings.equityHint}
            id="wacc-equity"
            label={strings.equity}
            numeric
            onChange={(equity) => update({ equity })}
            value={form.equity}
          />
          <TextField
            hint={strings.debtHint}
            id="wacc-debt"
            label={strings.debt}
            numeric
            onChange={(debt) => update({ debt })}
            value={form.debt}
          />
          <TextField
            id="wacc-preferred"
            label={strings.preferred}
            numeric
            onChange={(preferred) => update({ preferred })}
            value={form.preferred}
          />
          <SelectField
            id="wacc-method"
            label={strings.costMethodLabel}
            onChange={(method) => update({ method })}
            options={methods}
            value={form.method}
          />
          {form.method === "enter" ? (
            <TextField
              id="wacc-cost-equity"
              label={strings.costEquity}
              numeric
              onChange={(costEquity) => update({ costEquity })}
              value={form.costEquity}
            />
          ) : (
            <>
              <TextField
                id="wacc-rf"
                label={strings.riskFree}
                numeric
                onChange={(rf) => update({ rf })}
                value={form.rf}
              />
              <TextField
                id="wacc-beta"
                label={strings.beta}
                numeric
                onChange={(beta) => update({ beta })}
                value={form.beta}
              />
              <TextField
                id="wacc-rm"
                label={strings.marketReturn}
                numeric
                onChange={(rm) => update({ rm })}
                value={form.rm}
              />
            </>
          )}
          <TextField
            hint={strings.costDebtHint}
            id="wacc-cost-debt"
            label={strings.costDebt}
            numeric
            onChange={(costDebt) => update({ costDebt })}
            value={form.costDebt}
          />
          <TextField
            hint={strings.costPreferredHint}
            id="wacc-cost-preferred"
            label={strings.costPreferred}
            numeric
            onChange={(costPreferred) => update({ costPreferred })}
            value={form.costPreferred}
          />
          <TextField
            id="wacc-tax"
            label={strings.taxRate}
            numeric
            onChange={(tax) => update({ tax })}
            value={form.tax}
          />
        </div>
        <p className={HINT}>{strings.convention}</p>
      </section>
      {"input" in parsed && result ? (
        <Results
          capmInputs={parsed.capm}
          currency={currency}
          input={parsed.input}
          result={result}
          strings={strings}
        />
      ) : (
        <Notice>
          {"error" in parsed ? strings[parsed.error] : strings.needAmounts}
        </Notice>
      )}
    </div>
  );
}
